import type { KeyboardEvent, ReactNode } from 'react';

/**
 * The contract between `InlineField` and its editors, plus the two helpers
 * every editor shares. React-free on purpose (a separate module keeps the
 * component files fast-refreshable).
 */

export interface InlineEditorApi<T> {
  commit: (value: T) => void;
  cancel: () => void;
  saving: boolean;
  /** The row's display node — Popover-anchored editors render it as their anchor. */
  display: ReactNode;
}

/**
 * Parent-driven "open / focus this" request. A fresh nonce re-fires the same
 * target (the next-step card may ask for "value" twice in a row).
 */
export interface FocusRequest<Target extends string = string> {
  target: Target;
  nonce: number;
}

/**
 * Radix Select / Popover content is portaled out of the editor's DOM, yet
 * focus in there still belongs to the editor (a calendar day, a cmdk item).
 */
export const EDITOR_LAYER_SELECTOR = '[data-slot="select-content"], [data-slot="popover-content"]';

/** Enter commits, Escape cancels — and stays inside the editor (the sheet closes on Esc). */
export function editorKeyHandlers(commit: () => void, cancel: () => void) {
  return {
    onKeyDown: (event: KeyboardEvent<HTMLElement>) => {
      if (event.key === 'Enter') {
        event.preventDefault();
        commit();
      } else if (event.key === 'Escape') {
        event.preventDefault();
        event.stopPropagation();
        cancel();
      }
    },
  };
}
